import { useState, useEffect, useCallback } from "react";
import {
  Button, Space, Modal, Form, Select, DatePicker, InputNumber, Input, Upload, message,
} from "antd";
import { PlusOutlined, ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import KPICard, { KPIGrid } from "../../components/common/KPICard";
import ExportButton from "../../components/common/ExportButton";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import usePermission from "../../hooks/usePermission";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import { DAY_MODE_OPTIONS, formatOmr } from "./constants";
import {
  listCodRepayments, getCodRepaymentKpis, createCodRepayment,
  updateCodRepayment, deleteCodRepayment, exportCodRepayments,
} from "../../services/accounts.service";

const { RangePicker } = DatePicker;
const { TextArea } = Input;

/**
 * COD Repay — settlement with a project/aggregator for collected COD.
 *
 * Project-level, not rider-level: a repayment covers a date span of collections
 * for one project, which is how the reference system settles.
 */
export default function CODRepayTab() {
  const { canEdit, canDelete } = usePermission("accounts");
  const { options: projectOptions } = useSettingsDropdown("projects");
  const { options: bankOptions } = useSettingsDropdown("company-bank-accounts");
  const { options: paymentModeOptions } = useSettingsDropdown("payment-modes");

  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [project, setProject] = useState();
  const [dateRange, setDateRange] = useState(null);

  const [modal, setModal] = useState({ open: false, record: null });
  const [saving, setSaving] = useState(false);
  const [dayMode, setDayMode] = useState("single");
  const [attachment, setAttachment] = useState(null);
  const [form] = Form.useForm();

  const params = useCallback(
    (extra = {}) => ({
      search: search || undefined,
      project: project || undefined,
      fromDate: dateRange?.[0]?.format("YYYY-MM-DD"),
      toDate: dateRange?.[1]?.format("YYYY-MM-DD"),
      ...extra,
    }),
    [search, project, dateRange]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, kpiResponse] = await Promise.all([
        listCodRepayments(params({ page, per_page: pageSize })),
        getCodRepaymentKpis(params()),
      ]);
      setRows(list?.data?.records || []);
      setTotalCount(list?.data?.pagination?.total_count || 0);
      setKpis(kpiResponse?.data || {});
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not load COD repayments");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [params, page, pageSize]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    setPage(1);
  }, [search, project, dateRange]);

  const openModal = (record = null) => {
    setAttachment(null);
    setModal({ open: true, record });
    if (record) {
      setDayMode(record.dayMode || "single");
      form.setFieldsValue({
        project: record.project?._id,
        transactionDate: record.transactionDate ? dayjs(record.transactionDate) : null,
        dayMode: record.dayMode,
        collectedFrom: record.collectedFrom ? dayjs(record.collectedFrom) : null,
        collectedTo: record.collectedTo ? dayjs(record.collectedTo) : null,
        transactionId: record.transactionId,
        amountToPay: record.amountToPay,
        amountTransferred: record.amountTransferred,
        codOutstanding: record.codOutstanding,
        bankAccount: record.bankAccount?._id,
        paymentMode: record.paymentMode?._id,
        description: record.description,
      });
    } else {
      setDayMode("single");
      form.resetFields();
      form.setFieldsValue({
        transactionDate: dayjs(),
        dayMode: "single",
        collectedFrom: dayjs(),
        amountTransferred: 0,
      });
    }
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = {
        ...values,
        transactionDate: values.transactionDate.format("YYYY-MM-DD"),
        collectedFrom: values.collectedFrom.format("YYYY-MM-DD"),
        collectedTo: values.collectedTo?.format("YYYY-MM-DD"),
        ...(attachment ? { attachment } : {}),
      };
      if (modal.record) {
        await updateCodRepayment(modal.record._id, payload);
        message.success("COD repayment updated");
      } else {
        await createCodRepayment(payload);
        message.success("COD repayment recorded");
      }
      setModal({ open: false, record: null });
      fetchAll();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not save COD repayment");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    ConfirmDeleteModal({
      title: "Delete COD Repayment",
      content: `Delete ${record.repayId}?`,
      onConfirm: async () => {
        try {
          await deleteCodRepayment(record._id);
          message.success("COD repayment deleted");
          fetchAll();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Could not delete");
        }
      },
    });
  };

  const columns = [
    { title: "Repay ID", dataIndex: "repayId" },
    { title: "Project", dataIndex: "projectName" },
    { title: "Transaction Date", dataIndex: "transactionDateLabel" },
    { title: "Days", dataIndex: "dayModeLabel" },
    { title: "Collected From", dataIndex: "collectedFromLabel" },
    { title: "Collected To", dataIndex: "collectedToLabel" },
    { title: "Amount To Pay", dataIndex: "amountToPay", align: "right", render: formatOmr },
    {
      title: "Transferred",
      dataIndex: "amountTransferred",
      align: "right",
      render: formatOmr,
    },
    { title: "Balance", dataIndex: "balance", align: "right", render: formatOmr },
    {
      title: "COD Outstanding",
      dataIndex: "codOutstanding",
      align: "right",
      render: formatOmr,
    },
    { title: "Bank Account", dataIndex: "bankAccountName" },
    { title: "Transaction ID", dataIndex: "transactionId" },
  ];

  return (
    <div>
      <KPIGrid minWidth={170}>
        <KPICard label="Repayments" value={kpis.all} />
        <KPICard label="Amount To Pay" value={kpis.amountToPay} unit="OMR" />
        <KPICard label="Amount Transferred" value={kpis.amountTransferred} unit="OMR" tone="positive" />
        <KPICard label="Balance" value={kpis.balance} unit="OMR" tone="negative" allowNegative />
      </KPIGrid>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search repay ID or transaction"
          allowClear
          style={{ width: 260 }}
          onSearch={setSearch}
        />
        <Select
          placeholder="Project"
          allowClear
          style={{ width: 170 }}
          options={projectOptions}
          value={project}
          onChange={setProject}
        />
        <RangePicker format="DD/MM/YYYY" onChange={setDateRange} />
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>
          Refresh
        </Button>
        <ExportButton
          moduleName="COD_Repay"
          onExport={() => exportCodRepayments(params())}
        />
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            Add Repayment
          </Button>
        )}
      </Space>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={setPage}
        actions={{
          onEdit: canEdit ? openModal : undefined,
          onDelete: canDelete ? handleDelete : undefined,
          canEdit,
          canDelete,
        }}
      />

      <Modal
        open={modal.open}
        title={modal.record ? "Edit COD Repayment" : "Add COD Repayment"}
        onCancel={() => setModal({ open: false, record: null })}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save"
        width={640}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="project" label="Project" rules={[{ required: true }]}>
            <Select options={projectOptions} />
          </Form.Item>
          <Form.Item name="transactionDate" label="Transaction Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="dayMode" label="No. of Days">
            <Select options={DAY_MODE_OPTIONS} onChange={setDayMode} />
          </Form.Item>
          <Form.Item name="collectedFrom" label="Collected From" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          {dayMode === "multiple" && (
            <Form.Item
              name="collectedTo"
              label="Collected To"
              rules={[{ required: true, message: "Collected To is required for a multi-day repayment" }]}
            >
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
          )}
          <Form.Item name="amountToPay" label="Amount To Pay (OMR)" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={0} precision={3} />
          </Form.Item>
          <Form.Item
            name="amountTransferred"
            label="Amount Transferred (OMR)"
            extra="Balance is calculated automatically. A non-zero amount posts an outward ledger entry."
          >
            <InputNumber style={{ width: "100%" }} min={0} precision={3} />
          </Form.Item>
          <Form.Item name="codOutstanding" label="COD Outstanding (OMR)">
            <InputNumber style={{ width: "100%" }} precision={3} />
          </Form.Item>
          <Form.Item name="bankAccount" label="Bank Account">
            <Select options={bankOptions} allowClear />
          </Form.Item>
          <Form.Item name="paymentMode" label="Payment Mode">
            <Select options={paymentModeOptions} allowClear />
          </Form.Item>
          <Form.Item name="transactionId" label="Transaction ID">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} maxLength={1000} showCount />
          </Form.Item>
          <Form.Item label="Attachment">
            <Upload
              maxCount={1}
              beforeUpload={(file) => {
                setAttachment(file);
                return false;
              }}
              onRemove={() => setAttachment(null)}
              fileList={attachment ? [attachment] : []}
            >
              <Button icon={<UploadOutlined />}>Select file (max 2 MB)</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
